import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataExtensionField } from '../../../../../lib/common/custom-activity/dataExtensionTypes';
import {
    Activity,
    Schema,
} from '../../../../../lib/common/custom-activity/eventTypes';
import { trigger } from '../../../../../lib/common/postmonger/postmongerManager';

export interface SimpleConfigurationPageState {
    idField: string;
    evaluateField: string;
    additionalText: string;
    dirty: boolean;
}

const initialState: SimpleConfigurationPageState = {
    idField: '',
    evaluateField: '',
    additionalText: '',
    dirty: false,
};

interface DoneArgs {
    activity: Activity;
    schema: Schema;
}

export const simpleConfigurationPageSlice = createSlice({
    name: 'simpleConfigurationPage',
    initialState,
    reducers: {
        activityUpdated: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<Activity>,
        ) => {
            const activity = action.payload;

            // If we ever get an activity update during configuration, we can't know
            // what the state really is. We should just reset.
            state.dirty = false;

            // Set the profile type dropdown to the value provided in the activity. If
            // the profile type hasn't already been set in the activity, also dirty
            // the state so that the user can save it.
        },
        idFieldChanged: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<string>,
        ) => {
            const idField = action.payload;

            if (state.idField !== idField) {
                state.idField = idField;

                // only make it dirty if *BOTH* the id and profileType are set, and if they don't match
                if (
                    state.idField !== '' &&
                    state.evaluateField !== '' &&
                    state.idField !== state.evaluateField
                ) {
                    state.dirty = true;
                    trigger('setActivityDirtyState', true);
                } else {
                    state.dirty = false;
                }
            }
        },
        evaluateFieldChanged: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<string>,
        ) => {
            const evaluateField = action.payload;

            if (state.evaluateField !== evaluateField) {
                state.evaluateField = evaluateField;

                // only make it dirty if *BOTH* the idField and evaluateField are set, and if they don't match
                if (
                    state.idField !== '' &&
                    state.evaluateField !== '' &&
                    state.idField !== state.evaluateField
                ) {
                    state.dirty = true;
                    trigger('setActivityDirtyState', true);
                } else {
                    state.dirty = false;
                }
            }
        },
        additionalTextChanged: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<string>,
        ) => {
            const additionalText = action.payload;

            if (state.additionalText !== additionalText) {
                state.additionalText = additionalText;

                // only make it dirty if *BOTH* the idField and evaluateField are set, and if they don't match
                if (
                    state.idField !== '' &&
                    state.evaluateField !== '' &&
                    state.idField !== state.evaluateField
                ) {
                    state.dirty = true;
                    trigger('setActivityDirtyState', true);
                } else {
                    state.dirty = false;
                }
            }
        },
        done: (
            state: SimpleConfigurationPageState,
            action: PayloadAction<DoneArgs>,
        ) => {
            // Build a new activity with the updated data
            const activity = JSON.parse(
                JSON.stringify(action.payload.activity),
            );
            const schema = action.payload.schema;

            if (!state.dirty) {
                console.warn('Nothing to save.');
                return;
            }

            if (activity === null || schema === null) {
                const nullField = activity === null ? 'Activity' : 'Schema';
                console.error(
                    `Unable to finalize activity. ${nullField} is not defined.`,
                );
                return;
            }

            const IdFieldKey = state.idField; //getFieldKeyFromSchema(schema, 'Id');
            const EvaluateFieldKey = state.evaluateField; // getFieldKeyFromSchema(schema,'ProfileType',);
            const AdditionalTextKey = state.additionalText; // getFieldKeyFromSchema(schema,'ProfileType',);

            // Makre sure that we have at least the ID field and the Evaluate field
            if (IdFieldKey === null || EvaluateFieldKey === null) {
                const nullField =
                    IdFieldKey === null ? 'IdFieldKey' : 'EvaluateFieldKey';
                console.error(
                    `Unable to finalize activity. ${nullField} is not defined in activity.`,
                );
                return;
            }

            // Remove the old arguments that have since been renamed/removed
            deleteInArgument(activity, 'id');
            deleteInArgument(activity, 'idType');
            deleteInArgument(activity, 'profileType');
            deleteInArgument(activity, 'textType');

            // Now, the arguments that we care about
            addOrUpdateInArgument(
                activity,
                'idField',
                '{{' + IdFieldKey + '}}',
            );
            addOrUpdateInArgument(
                activity,
                'evaluateField',
                '{{' + EvaluateFieldKey + '}}',
            );
            addOrUpdateInArgument(
                activity,
                'additionalText',
                AdditionalTextKey,
            );

            activity.metaData['isConfigured'] = true;

            trigger('updateActivity', activity);
            trigger('requestInspectorClose');
        },
        cancel: (state: SimpleConfigurationPageState) => {
            // Tell Journey Builder that this activity has no changes.
            // we wont be prompted to save changes when the inspector closes
            trigger('setActivityDirtyState', false);

            // now request that Journey Builder closes the inspector/drawer
            trigger('requestInspectorClose');
        },
    },
});

export const {
    activityUpdated,
    idFieldChanged,
    evaluateFieldChanged,
    additionalTextChanged,
    done,
    cancel,
} = simpleConfigurationPageSlice.actions;

function findField(schema: Schema, key: string): DataExtensionField | null {
    return schema.find((field) => field.name === key) || null;
}

function getFieldKeyFromSchema(schema: Schema, key: string): string | null {
    if (schema === null) {
        return null;
    }

    const field = findField(schema, key);
    if (field === null) {
        return null;
    }
    return field.key;
}

function getInArgumentValue(
    activity: Activity,
    argument: string,
): string | null {
    const inArguments = activity?.arguments?.execute?.inArguments;
    if (inArguments === undefined) {
        return null;
    }

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if the argument doesn't exist
    if (executeArg === undefined || !(argument in executeArg)) {
        return null;
    }

    return executeArg[argument];
}

/**
 * Adds or updates an inArgument with the name `argument` and value `value`.
 * @param activity The activity to update
 * @param argument The argument name
 * @param value The argument value
 * @returns Whether anything was changed.
 */
function addOrUpdateInArgument(
    activity: Activity,
    argument: string,
    value: string,
): boolean {
    if (activity.arguments === undefined) {
        activity.arguments = {};
    }

    if (activity.arguments.execute === undefined) {
        activity.arguments.execute = {};
    }

    if (activity.arguments.execute.inArguments === undefined) {
        activity.arguments.execute.inArguments = [];
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if we don't need to change anything
    if (
        executeArg !== undefined &&
        argument in executeArg &&
        executeArg[argument] === value
    ) {
        return false;
    }

    if (executeArg === undefined) {
        executeArg = {};
        inArguments.push(executeArg);
    }

    executeArg[argument] = value;
    return true;
}

function deleteInArgument(activity: Activity, argument: string): void {
    if (activity.arguments === undefined) {
        activity.arguments = {};
    }

    if (activity.arguments.execute === undefined) {
        activity.arguments.execute = {};
    }

    if (activity.arguments.execute.inArguments === undefined) {
        activity.arguments.execute.inArguments = [];
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll delete the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    if (executeArg !== undefined && argument in executeArg) {
        delete executeArg[argument];
    }
}

/**
 * Adds or updates an inArgument with the name `argument` and value `value`.
 * @param activity The activity to update
 * @param argument The argument name
 * @param value The argument value
 * @returns Whether anything was changed.
 
function removeInArgument(
    activity: Activity,
    argument: string,
    value: string,
): boolean {
    if (activity.arguments === undefined) {
        activity.arguments = {};
    }

    if (activity.arguments.execute === undefined) {
        activity.arguments.execute = {};
    }

    if (activity.arguments.execute.inArguments === undefined) {
        activity.arguments.execute.inArguments = [];
    }

    const inArguments = activity.arguments.execute.inArguments;
    inArguments.

    // See if we already have an argument with this name
    let executeArg = inArguments.find((item: any) => argument in item);

    // If we found it, get rid of it!
    if (
        executeArg !== undefined &&
        argument in executeArg &&
        executeArg[argument] === value
    ) {
        return false;
    }

    if (executeArg === undefined) {
        executeArg = {};
        inArguments.pop();
    }

    executeArg[argument] = value;
    return true;
}*/
